import { useTranslation } from "react-i18next";
import { CopyButton } from "../internal-ui/copy-button";
import { ImageWithFallback } from "../internal-ui/image-with-fallback";
import { useDisplayPrice } from "../lib/currency";
import { PW, pewterField, pewterPanel } from "../internal-ui/pewter-shell";

import { Icon } from "@majourneys/icons";
/** The popup's band: the sheet's tone inside, the float shadow under it. */
const PANEL = pewterPanel(24, { fill: PW.sheetFill, shadow: "0 18px 44px -18px rgba(17,14,57,0.45)" });

interface ShareModalProps {
  url: string;
  title: string;
  thumbnail?: string;
  cityName: string;
  price: number;
  onClose: () => void;
}

export function ShareModal({ url, title, thumbnail, cityName, price, onClose }: ShareModalProps) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const wa = `https://wa.me/?text=${encodeURIComponent(`${title} — ${url}`)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  /*
   * iMessage / SMS. `sms:?&body=` is the form both iOS and Android accept —
   * iOS wants `sms:&body=`, Android `sms:?body=`, and this one satisfies both.
   * On a Mac it opens Messages, which IS iMessage, so it is not phone-only.
   */
  const sms = `sms:?&body=${encodeURIComponent(`${title} — ${url}`)}`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("activity.share")}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      {/* A pewter card with the title in navy, like every other popup (owner,
          2026-10-11): the navy header band it had was the only one on the site. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full min-w-0 max-w-md overflow-hidden"
        style={PANEL}
      >
        <div className="flex items-center justify-between gap-3 px-6 pb-1 pt-5">
          <h2 className="min-w-0 font-sans text-[18px] font-bold" style={{ color: PW.navy }}>
            {t("activity.share_experience")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
            style={{ background: PW.softTint, color: PW.navy }}
          >
            <Icon name="ri-close-line" className="text-[20px]" aria-hidden="true" />
          </button>
        </div>
        <div className="px-6 pb-6 pt-4">
          <div className="flex gap-3 p-3" style={pewterField(16)}>
            <ImageWithFallback src={thumbnail} alt={title} className="h-16 w-16 rounded-lg object-cover" />
            <div className="flex flex-1 flex-col">
              <h3 className="font-sans text-[14px] font-semibold text-text-primary line-clamp-2">{title}</h3>
              <p className="font-sans text-[12px] text-text-muted">{cityName}</p>
              <p className="mt-1 font-sans text-[14px] font-bold text-primary">{displayPrice(price)}</p>
            </div>
          </div>

          <div className="mt-4">
            <p className="font-sans text-[12px] text-text-muted">{t("activity.copy_link")}</p>
            <div className="mt-2">
              <CopyButton value={url} label={t("activity.copy_link")} />
            </div>
          </div>

          <div className="mt-4">
            <p className="font-sans text-[12px] text-text-muted">{t("activity.quick_share")}</p>
            <div className="mt-2 flex gap-3">
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#16a34a] text-white"
                aria-label="WhatsApp"
              >
                <Icon name="ri-whatsapp-line" className="text-xl" />
              </a>
              <a
                href={fb}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1877f2] text-white"
                aria-label="Facebook"
              >
                <Icon name="ri-facebook-fill" className="text-xl" />
              </a>
              {/* Apple's own Messages green. It sits beside WhatsApp's darker
                  green on purpose — using an invented colour to avoid the
                  adjacency would be less recognisable, not more. No
                  `target="_blank"`: a `sms:` link handed to a new tab leaves
                  an empty one behind. */}
              <a
                href={sms}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-[#34C759] text-white"
                aria-label="iMessage"
              >
                <Icon name="ri-chat-1-line" className="text-xl" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
